import axios from "axios";
import { previewMode } from "./previewConfig";

// A Cloudflare preview never inherits the production API URL by accident.
// Only a separately configured staging build can target the staging API.
const BACKEND_URL = previewMode === "staging" || previewMode === "demo"
  ? ""
  : (process.env.REACT_APP_BACKEND_URL || "");
export const API = `${BACKEND_URL}/api`;

// Do not allow the synthetic public demo to issue backend requests, even if a
// feature accidentally imports a legacy API function.
const publicDemoError = () => Promise.reject(new Error("Public demo has no backend connection."));

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

if (previewMode === "demo") {
  api.interceptors.request.use(() => publicDemoError());
}

// Clients
export const listClients = () => api.get("/clients").then((r) => r.data);
export const createClient = (data) => api.post("/clients", data).then((r) => r.data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data).then((r) => r.data);
export const deleteClient = (id) => api.delete(`/clients/${id}`).then((r) => r.data);

// Deadlines
export const listDeadlines = (params = {}) =>
  api.get("/deadlines", { params }).then((r) => r.data);
export const createDeadline = (data) => api.post("/deadlines", data).then((r) => r.data);
export const updateDeadline = (id, data) => api.put(`/deadlines/${id}`, data).then((r) => r.data);
export const deleteDeadline = (id) => api.delete(`/deadlines/${id}`).then((r) => r.data);

// Dashboard
export const getSummary = () => api.get("/dashboard/summary").then((r) => r.data);

// Reports (Ficha)
export const getReport = (clientId) =>
  api.get(`/reports/${clientId}`).then((r) => r.data);
export const saveReport = (clientId, data) =>
  api.put(`/reports/${clientId}`, data).then((r) => r.data);

// Certificate alerts
export const getCertificateAlerts = () =>
  api.get("/dashboard/certificates").then((r) => r.data);

export const getClient = (id) => api.get(`/clients/${id}`).then((r) => r.data);

// Users (admin)
export const listUsers = () => api.get("/users").then((r) => r.data);
export const createUser = (data) => api.post("/users", data).then((r) => r.data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data).then((r) => r.data);
export const deleteUser = (id) => api.delete(`/users/${id}`).then((r) => r.data);

// Tasks
export const listTasks = (params = {}) =>
  api.get("/tasks", { params }).then((r) => r.data);
export const createTask = (data) => api.post("/tasks", data).then((r) => r.data);
export const updateTask = (id, data) => api.put(`/tasks/${id}`, data).then((r) => r.data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`).then((r) => r.data);

// History (admin)
export const listHistory = (limit = 200) =>
  api.get("/history", { params: { limit } }).then((r) => r.data);

// Organizations (super-admin only)
export const listOrganizations = () =>
  api.get("/organizations").then((r) => r.data);
export const createOrganization = (data) =>
  api.post("/organizations", data).then((r) => r.data);
export const updateOrganization = (id, data) =>
  api.put(`/organizations/${id}`, data).then((r) => r.data);
export const deleteOrganization = (id) =>
  api.delete(`/organizations/${id}`).then((r) => r.data);

// Observations
export const listObservations = (clientId) =>
  api.get(`/clients/${clientId}/observations`).then((r) => r.data);
export const createObservation = (clientId, text) =>
  api.post(`/clients/${clientId}/observations`, { text }).then((r) => r.data);
export const deleteObservation = (clientId, obsId) =>
  api.delete(`/clients/${clientId}/observations/${obsId}`).then((r) => r.data);

// Global search
export const globalSearch = (q) =>
  api.get("/search", { params: { q } }).then((r) => r.data);

// Today panel
export const getToday = () =>
  api.get("/dashboard/today").then((r) => r.data);

// Client Files (Contrato Social + Documentos)
export const listClientFiles = (clientId, category) =>
  api.get(`/clients/${clientId}/files`, { params: category ? { category } : {} }).then((r) => r.data);

export const uploadClientFile = (clientId, category, file, onProgress) => {
  const fd = new FormData();
  fd.append("category", category);
  fd.append("file", file);
  return api.post(`/clients/${clientId}/files`, fd, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  }).then((r) => r.data);
};

export const renameClientFile = (clientId, fileId, customName) =>
  api.patch(`/clients/${clientId}/files/${fileId}`, { custom_name: customName }).then((r) => r.data);

export const deleteClientFile = (clientId, fileId) =>
  api.delete(`/clients/${clientId}/files/${fileId}`).then((r) => r.data);

export const shareClientFile = (clientId, fileId) =>
  api.post(`/clients/${clientId}/files/${fileId}/share`).then((r) => r.data);

export const downloadFileUrl = (clientId, fileId) => {
  const backend = BACKEND_URL;
  return `${backend}/api/clients/${clientId}/files/${fileId}/download`;
};

export const publicShareUrl = (token) => {
  const backend = BACKEND_URL;
  return `${backend}/api/public/shared/${token}`;
};

// Common client tag suggestions
export const COMMON_TAGS = [
  "Urgente",
  "Premium",
  "MEI",
  "Simples Nacional",
  "Lucro Presumido",
  "Lucro Real",
  "Pendente",
  "Novo",
];

// Helpers
export const TAX_TYPES = [
  "Alvará Sanitário",
  "Certificado do Bombeiro",
  "Licença Ambiental",
  "Alvará de Licença",
];

export const daysUntil = (isoDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(isoDate + "T00:00:00");
  const diff = Math.round((due - today) / (1000 * 60 * 60 * 24));
  return diff;
};

export const statusRowClass = (deadline) => {
  if (deadline.status === "completed") return "row-completed";
  const d = daysUntil(deadline.due_date);
  if (d < 0) return "row-overdue";
  if (d <= 45) return "row-soon";
  return "row-normal";
};

export const formatBRDate = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
};
